import { RefObject, useRef, useState, MouseEvent } from 'react';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { legendRules } from '@components/machines/components/timeline/calculations';
import { TimelineFetchFilters, TimelineSizes } from '@interfaces/timeline';
import { localTime } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { setSelectedChartDate, setSelectedChartDateEnd } from '@reducers/machinesV2';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { UseTimelineViewModelParams } from './useTimelineViewModel';
import { Popup } from '@components/machines/components/timeline';

interface UseManageTimelineActionsParams extends Pick<UseTimelineViewModelParams, 'endTime' | 'startTime' | 'yearIsEnabled'> {
  fetchData?: (params: TimelineFetchFilters) => Promise<void>;
  triggerHidePopup: (type: Popup) => void;
  sizes: Pick<TimelineSizes, 'blockWidth'>;
}

export const useManageTimelineActions = ({ fetchData, endTime, startTime, yearIsEnabled, triggerHidePopup, sizes }: UseManageTimelineActionsParams) => {
  const dispatch = useAppDispatch();

  const { timePeriod: selectedTime } = useHandleParams();

  const { blockWidth } = sizes;

  // Refs
  const eventsContainerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const timelineRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const lineRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const sectionRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const timeXRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const abortControllerEventStart = useRef<AbortController>();
  const abortControllerEventEnd = useRef<AbortController>();
  const abortControllerNcFile = useRef<AbortController>();

  // State
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [machineIdsToShowEvents, setMachineIdsToShowEvents] = useState<number[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<NcFileBatchEventType>(NcFileBatchEventType.CYCLE);

  const rules = legendRules({ startTime, endTime, blockWidth, selectedTime });

  // Events functions
  const onShowEventsPress = (machineId?: number) => {
    if (machineId === undefined) return;
    setMachineIdsToShowEvents((prev) =>
      prev.includes(machineId)
        ? prev.filter(id => id !== machineId)
        : [...prev, machineId]
    );
  };

  const closeEventsForMachine = (machineId: number) => {
    setMachineIdsToShowEvents((prev) => prev.filter(id => id !== machineId));
  };

  const isShowingEventsForMachine = (machineId: number) => {
    return machineIdsToShowEvents.includes(machineId);
  };

  // Actions
  const setCursorX = (value: number | null) => {
    if (lineRef.current) {
      if (value) {
        lineRef.current.style.display = 'block';
        lineRef.current.style.left = value - 1 + 'px';
      } else {
        lineRef.current.style.display = 'none';
      }
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const timelineRect = timelineRef?.current?.getBoundingClientRect();
    if (timelineRect) {
      const currentX = e.clientX - timelineRect.left;
      const isInBounds = currentX > 0 && currentX < timelineRect.width;

      if (isInBounds) {
        const startSeconds = currentX / rules.secondWidth;
        const time = localTime(startTime);
        const start = time.add(startSeconds, 's').format('HH:mm');
        timeXRef?.current && (timeXRef.current.innerHTML = start);

        if (isDragging) {
          setCursorX(null);
          const width = Math.abs(currentX - startX);
          sectionRef?.current && (sectionRef.current.style.width = width + 'px');
          if (currentX < startX) {
            sectionRef?.current && (sectionRef.current.style.left = currentX + 'px');
          }
          return;
        }

        setCursorX(e.clientX - timelineRect.left);
      }
    }
  };

  const selectSegment = async () => {
    const left = Number(sectionRef?.current?.style.left?.replace('px', '')) || 0;
    const width = Number(sectionRef?.current?.style.width?.replace('px', '')) || 0;
    if (!width) {
      return;
    }

    const startSeconds = left / rules.secondWidth;
    const durationSeconds = width / rules.secondWidth;
    const time = localTime(startTime);

    if (durationSeconds <= 60) {
      sectionRef?.current && (sectionRef.current.style.width = 0 + 'px');
      return;
    }

    const start = time.add(startSeconds, 's').startOf('m').format();
    dispatch(setSelectedChartDate(start));
    const end = time.add(durationSeconds, 's').startOf('m').format();
    dispatch(setSelectedChartDateEnd(end));

    if (fetchData) {
      await fetchData({ start, end });
    }
  };

  const selectRange = async ({ start, end }: TimelineFetchFilters) => {
    dispatch(setSelectedChartDate(start));
    dispatch(setSelectedChartDateEnd(localTime(end).format()));

    if (fetchData) {
      await fetchData({ start, end });
    }
  };

  const onMouseLeave = () => {
    if (isDragging) {
      selectSegment();
    }
    setIsDragging(false);
    setStartX(0);
    setCursorX(null);
    if (sectionRef?.current) {
      sectionRef.current.style.width = '0px';
    }
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    const timelineRect = timelineRef?.current?.getBoundingClientRect();
    triggerHidePopup?.('productivityStatus');
    triggerHidePopup?.('ncFileName');

    if (timelineRect && sectionRef?.current) {
      const newStartX = e.clientX - timelineRect.left;
      const isInBounds = newStartX > 0 && newStartX < timelineRect.width;
      sectionRef.current.style.left = newStartX + 'px';
      sectionRef.current.style.width = '0px';
      if (isInBounds) {
        setIsDragging(true);
        setStartX(newStartX);
      }
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      selectSegment();
    }
    setIsDragging(false);
    setStartX(0);
    if (sectionRef?.current) {
      sectionRef.current.style.width = '0px';
    }
  };

  return {
    // State
    startX,
    isDragging,
    selectedTime,
    machineIdsToShowEvents,
    selectedEventType,

    // Refs
    eventsContainerRef,
    timelineRef,
    lineRef,
    sectionRef,
    timeXRef,
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,

    // Timeline data
    startTime,
    endTime,
    rules,
    yearIsEnabled,

    // Setters
    setStartX,
    setIsDragging,
    setMachineIdsToShowEvents,
    setSelectedEventType,

    // Actions
    setCursorX,
    handleMouseMove,
    selectSegment,
    selectRange,
    onMouseLeave,
    handleMouseDown,
    handleMouseUp,
    onShowEventsPress,
    closeEventsForMachine,
    isShowingEventsForMachine,
  };
};
